import React, { useEffect, useMemo, useState } from "react";
import SummaryApi from "../common/index";

// ────────────────────────────────────────────────
// THEME — same rules as AllCities.js / InterestedStudents.js
// primary → header text, primary buttons, table headers
// actionBlue → table Edit button
// actionGreen → success / active state
// actionRed → danger / inactive state
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionYellow: '#D97706',
  actionYellowHover: '#B45309',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionPurple: '#7C3AED',
  actionPurpleHover: '#6D28D9',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
  disabledGray: '#D1D5DB',
};

const emptyForm = { checklist_name: "", is_active: 1 };

const CallCheckList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchChecklists = async () => {
    setLoading(true);
    try {
      const res = await fetch(SummaryApi.GetAdminChecklists.url, {
        method: SummaryApi.GetAdminChecklists.method.toUpperCase(),
      });
      const result = await res.json();
      if (result.status) setItems(result.data || []);
      else showToast("error", result.message || "Failed to load checklists");
    } catch (err) {
      showToast("error", "Unable to reach the server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecklists();
  }, []);

  const stats = useMemo(() => {
    const active = items.filter((i) => Number(i.is_active) === 1).length;
    return { total: items.length, active, inactive: items.length - active };
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const matchName = i.checklist_name
        .toLowerCase()
        .includes(search.trim().toLowerCase());
      const matchStatus =
        statusFilter === "all" || Number(i.is_active) === Number(statusFilter);
      return matchName && matchStatus;
    });
  }, [items, search, statusFilter]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      checklist_name: item.checklist_name,
      is_active: Number(item.is_active),
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const callApi = async (api, body) => {
    const res = await fetch(api.url, {
      method: api.method.toUpperCase(),
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.checklist_name.trim();
    if (!name) {
      setFormError("Checklist name is required");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const result = editing
        ? await callApi(SummaryApi.UpdateChecklist, {
          checklist_id: editing.checklist_id,
          checklist_name: name,
          is_active: form.is_active,
        })
        : await callApi(SummaryApi.CreateChecklist, {
          checklist_name: name,
          is_active: form.is_active,
        });

      if (result.status) {
        showToast("success", result.message);
        setModalOpen(false);
        fetchChecklists();
      } else {
        setFormError(result.message || "Something went wrong");
      }
    } catch (err) {
      setFormError("Unable to reach the server");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (item) => {
    const next = Number(item.is_active) === 1 ? 0 : 1;
    try {
      const result = await callApi(SummaryApi.UpdateChecklist, {
        checklist_id: item.checklist_id,
        checklist_name: item.checklist_name,
        is_active: next,
      });
      if (result.status) {
        setItems((prev) =>
          prev.map((i) =>
            i.checklist_id === item.checklist_id ? { ...i, is_active: next } : i
          )
        );
        showToast("success", next === 1 ? "Checklist activated" : "Checklist deactivated");
      } else {
        showToast("error", result.message || "Failed to update status");
      }
    } catch (err) {
      showToast("error", "Unable to reach the server");
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d.replace(" ", "T"));
    return isNaN(date)
      ? d
      : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
  };

  return (
    <div className="min-h-screen w-full">
      {/* Toast */}
      {toast && (
        <div
          style={{
            backgroundColor:
              toast.type === "success" ? THEME.actionGreen : THEME.actionRed,
          }}
          className="fixed top-5 right-5 z-[60] px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white"
        >
          {toast.text}
        </div>
      )}

      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: THEME.primaryLight }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: THEME.primary }}
              >
                <svg
                  className="w-4 h-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1
                className="text-2xl md:text-3xl font-bold"
                style={{ color: THEME.primary }}
              >
                Checklist Management
              </h1>
              <p className="text-xs text-gray-500">
                Create and manage the checklist items used for parents and employees.
              </p>
            </div>
          </div>

          <button
            onClick={openAdd}
            style={{ backgroundColor: THEME.primary }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm transition-opacity"
          >
            <span className="text-lg leading-none">+</span> Add Checklist
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="rounded-xl bg-blue-50 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5h11M9 12h11M9 19h11M4 5h.01M4 12h.01M4 19h.01" />
              </svg>
            </div>
            <div>
              <p
                className="text-2xl font-bold"
                style={{ color: THEME.primary }}
              >
                {stats.total}
              </p>
              <p className="text-sm font-semibold text-gray-700">Total Checklists</p>
              <p className="text-xs text-gray-500">All checklist items</p>
            </div>
          </div>
          <div className="rounded-xl bg-green-50 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p
                className="text-2xl font-bold"
                style={{ color: THEME.primary }}
              >
                {stats.active}
              </p>
              <p className="text-sm font-semibold text-gray-700">Active</p>
              <p className="text-xs text-gray-500">Currently in use</p>
            </div>
          </div>
          <div className="rounded-xl bg-red-50 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <p
                className="text-2xl font-bold"
                style={{ color: THEME.primary }}
              >
                {stats.inactive}
              </p>
              <p className="text-sm font-semibold text-gray-700">Inactive</p>
              <p className="text-xs text-gray-500">Not in use</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search checklist..."
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ color: THEME.primary }}
            className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
          >
            <option value="all">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm text-gray-700">
            <thead className="bg-gray-200">
              <tr className="text-left">
                <th className="px-4 py-2 text-left font-semibold whitespace-nowrap w-16" style={{ color: THEME.primary }}>S.No</th>
                <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Checklist Name</th>
                <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Status</th>
                <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Created On</th>
                <th className="px-4 py-2 font-semibold whitespace-nowrap text-center w-32" style={{ color: THEME.primary }}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-10">
                    <div className="flex justify-center items-center">
                      <div
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: THEME.primary }}
                      />
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-10">
                    <div className="flex flex-col items-center text-center">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                        style={{ backgroundColor: THEME.primaryLight, color: THEME.primary }}
                      >
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5h11M9 12h11M9 19h11M4 5h.01M4 12h.01M4 19h.01" />
                        </svg>
                      </div>
                      <p className="text-sm font-bold" style={{ color: THEME.primary }}>
                        No checklists found
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Try changing your search or filter.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((item, idx) => {
                  const active = Number(item.is_active) === 1;
                  return (
                    <tr
                      key={item.checklist_id}
                      className="hover:bg-slate-50 transition"
                    >
                      <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {item.checklist_name}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleStatus(item)}
                          title="Click to change status"
                          className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1 rounded-full text-xs font-semibold transition-colors ${active
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-red-100 text-red-800 hover:bg-red-200"
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${active ? "bg-green-600" : "bg-red-500"
                              }`}
                          />
                          {active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {formatDate(item.created_date)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => openEdit(item)}
                          style={{ backgroundColor: THEME.actionBlue }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = THEME.actionBlueHover)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = THEME.actionBlue)
                          }
                          className="text-white px-3 py-2 rounded-md text-xs font-semibold transition-colors shadow-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={closeModal}
        >
          <div
            className="mx-auto my-10 mt-[100px] w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2
                className="text-xl font-semibold"
                style={{ color: THEME.primary }}
              >
                {editing ? "Edit Checklist" : "Add Checklist"}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-600 hover:text-gray-800 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label
                  className="block text-sm font-semibold mb-1.5"
                  style={{ color: THEME.primary }}
                >
                  Checklist Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.checklist_name}
                  onChange={(e) =>
                    setForm({ ...form, checklist_name: e.target.value })
                  }
                  placeholder="e.g. Food, Hostel, Placement"
                  autoFocus
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-300 px-4 py-3">
                <div>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Status
                  </p>
                  <p className="text-xs text-gray-500">
                    {form.is_active === 1 ? "Active" : "Inactive"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setForm({ ...form, is_active: form.is_active === 1 ? 0 : 1 })
                  }
                  style={{
                    backgroundColor: form.is_active === 1 ? THEME.actionGreen : THEME.disabledGray,
                  }}
                  className="relative w-12 h-6 rounded-full transition-colors"
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active === 1 ? "translate-x-6" : ""
                      }`}
                  />
                </button>
              </div>

              {formError && (
                <p className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">
                  {formError}
                </p>
              )}

              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ backgroundColor: saving ? THEME.disabledGray : THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : editing ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallCheckList;